import { useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

import { ApiError, api } from "../api/client";
import { Button, Card, PageHeader, cx } from "../components/ui";
import type { Money, ProductWrite } from "../api/types";

const RESOURCE_TYPES = [
  ["", "None"],
  ["treatment_room", "Treatment room"],
  ["operatory", "Operatory"],
  ["chair", "Chair"],
  ["wash_station", "Wash station"],
  ["bed", "Bed"],
  ["studio", "Studio"],
] as const;

/**
 * Create and edit a product.
 *
 * One component for both, because the fields and the rules are the same — two
 * forms drift, and the drift shows up as a field the edit form forgets.
 *
 * `account` is never sent. The tenant comes from the token.
 */
export function ProductFormPage() {
  const { id } = useParams<{ id: string }>();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existing = useQuery({
    queryKey: ["product", id],
    queryFn: ({ signal }) => api.product(id!, signal),
    enabled: editing,
  });

  const options = useQuery({
    queryKey: ["product-options"],
    queryFn: ({ signal }) => api.productOptions(signal),
    // Used to suggest categories that already exist, so the owner does not end
    // up with "Massage", "massage" and "MASSAGE".
    staleTime: 5 * 60_000,
  });

  if (editing && existing.isPending) {
    return (
      <>
        <BackLink editing={editing} />
        <PageHeader title="Loading…" />
      </>
    );
  }

  if (editing && existing.isError) {
    return (
      <>
        <BackLink editing={editing} />
        <PageHeader title="Not available" subtitle="That product could not be loaded." />
      </>
    );
  }

  return (
    <>
      <BackLink editing={editing} />
      <PageHeader
        title={editing ? `Edit ${existing.data?.name}` : "New product"}
        subtitle={
          editing
            ? existing.data?.from_preset
              ? "From your industry starter pack. Editing it keeps it identifiable."
              : undefined
            : "What you sell, what it costs you, and how long it takes."
        }
      />
      <ProductForm
        product={existing.data}
        knownCategories={[...new Set((options.data ?? []).map((o) => o.category).filter(Boolean))]}
        onDone={(saved) => {
          void queryClient.invalidateQueries({ queryKey: ["products"] });
          void queryClient.invalidateQueries({ queryKey: ["catalog-summary"] });
          void queryClient.invalidateQueries({ queryKey: ["product-options"] });
          navigate(`/catalog/products/${saved.id}/edit`);
        }}
      />
    </>
  );
}

function BackLink({ editing }: { editing: boolean }) {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  return (
    <button
      onClick={() => navigate(editing && id ? "/catalog" : "/catalog")}
      className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900"
    >
      <ArrowLeft size={14} /> Back to catalog
    </button>
  );
}

function ProductForm({
  product,
  knownCategories,
  onDone,
}: {
  product: import("../api/types").Product | undefined;
  knownCategories: string[];
  onDone: (saved: import("../api/types").Product) => void;
}) {
  const navigate = useNavigate();

  const [form, setForm] = useState<ProductWrite>(() => ({
    name: product?.name ?? "",
    category: product?.category ?? "",
    description: product?.description ?? "",
    duration_minutes: product?.duration_minutes ?? null,
    buffer_minutes: product?.buffer_minutes ?? 0,
    price: product?.price ?? undefined,
    cost_price: product?.cost_price ?? undefined,
    requires_resource_type: product?.requires_resource_type ?? "",
    requires_intake_form: product?.requires_intake_form ?? false,
    requires_consent: product?.requires_consent ?? false,
    rebooking_interval_days: product?.rebooking_interval_days ?? null,
    is_bookable: product?.is_bookable ?? true,
    is_taxable: product?.is_taxable ?? true,
    is_active: product?.is_active ?? true,
  }));

  const save = useMutation({
    mutationFn: (body: ProductWrite) =>
      product ? api.updateProduct(product.id, body) : api.createProduct(body),
    onSuccess: onDone,
  });

  const error = save.error instanceof ApiError ? save.error : null;
  const fieldErrors = error?.fields ?? {};

  function set<K extends keyof ProductWrite>(key: K, value: ProductWrite[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    save.mutate({
      ...form,
      // Empty strings are not nulls: a CharField given "" stores "", while
      // ``None`` on a non-nullable field is a 500.
      category: form.category || "",
      requires_resource_type: form.requires_resource_type || "",
    });
  }

  // Live margin, so the owner sees the consequence while typing rather than
  // discovering it in a month-end report. The server recomputes it; this is a
  // preview, not the value of record.
  const price = amountOf(form.price);
  const cost = amountOf(form.cost_price);
  const margin =
    price === null || price === 0 || cost === null
      ? null
      : Math.round(((price - cost) / price) * 1000) / 10;

  return (
    <form onSubmit={onSubmit} className="grid gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">What it is</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id="name"
              label="Name"
              required
              autoFocus={!product}
              value={form.name ?? ""}
              onChange={(v) => set("name", v)}
              errors={fieldErrors.name}
              hint="What the customer would call it."
            />
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-slate-700">
                Category
              </label>
              <input
                id="category"
                list="known-categories"
                value={form.category ?? ""}
                onChange={(e) => set("category", e.target.value)}
                aria-invalid={fieldErrors.category?.length ? true : undefined}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 placeholder:text-slate-400 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              />
              <datalist id="known-categories">
                {knownCategories.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
              {knownCategories.length > 0 ? (
                <p className="mt-1 text-xs text-slate-500">
                  Grouping is yours. Existing: {knownCategories.slice(0, 3).join(", ")}.
                </p>
              ) : null}
            </div>
            <div className="sm:col-span-2">
              <TextField
                id="description"
                label="Description"
                value={form.description ?? ""}
                onChange={(v) => set("description", v)}
                errors={fieldErrors.description}
              />
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="mb-4 text-sm font-semibold">Time it takes</h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <NumberField
              id="duration_minutes"
              label="Duration (minutes)"
              value={form.duration_minutes}
              onChange={(v) => set("duration_minutes", v)}
              errors={fieldErrors.duration_minutes}
              hint="Leave blank to use your account default."
            />
            <NumberField
              id="buffer_minutes"
              label="Buffer after (minutes)"
              value={form.buffer_minutes ?? 0}
              onChange={(v) => set("buffer_minutes", v ?? 0)}
              errors={fieldErrors.buffer_minutes}
              hint="Cleanup. Blocks the calendar but is not billed."
            />
            <NumberField
              id="rebooking_interval_days"
              label="Rebooking interval (days)"
              value={form.rebooking_interval_days}
              onChange={(v) => set("rebooking_interval_days", v)}
              errors={fieldErrors.rebooking_interval_days}
              hint="How long until they would come back."
            />
            <div>
              <label htmlFor="requires_resource_type" className="block text-sm font-medium text-slate-700">
                Needs a resource
              </label>
              <select
                id="requires_resource_type"
                value={form.requires_resource_type ?? ""}
                onChange={(e) => set("requires_resource_type", e.target.value)}
                className="mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset ring-slate-300 focus:ring-2 focus:ring-inset focus:ring-slate-900"
              >
                {RESOURCE_TYPES.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </Card>

        <Card>
          <h2 className="mb-1 text-sm font-semibold">Requirements</h2>
          <p className="mb-4 text-xs text-slate-500">
            What must be true before this can be booked.
          </p>
          <div className="space-y-2.5">
            <Check
              id="requires_intake_form"
              label="Needs an intake form"
              checked={form.requires_intake_form ?? false}
              onChange={(v) => set("requires_intake_form", v)}
            />
            <Check
              id="requires_consent"
              label="Needs signed consent"
              checked={form.requires_consent ?? false}
              onChange={(v) => set("requires_consent", v)}
            />
            <Check
              id="is_bookable"
              label="Can be booked as an appointment"
              checked={form.is_bookable ?? true}
              onChange={(v) => set("is_bookable", v)}
            />
            <Check
              id="is_taxable"
              label="Tax applies"
              checked={form.is_taxable ?? true}
              onChange={(v) => set("is_taxable", v)}
            />
          </div>
        </Card>
      </div>

      <div className="space-y-6">
        <Card>
          <h2 className="mb-4 text-sm font-semibold">Money</h2>
          <div className="space-y-4">
            <MoneyField
              id="price"
              label="Price"
              value={form.price}
              onChange={(v) => set("price", v)}
              errors={fieldErrors.price}
            />
            <MoneyField
              id="cost_price"
              label="Cost to deliver"
              value={form.cost_price}
              onChange={(v) => set("cost_price", v)}
              errors={fieldErrors.cost_price}
              hint="Materials and consumables. This is what makes margin real."
            />

            <div className="rounded-md bg-slate-50 p-3">
              <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                Margin
              </p>
              <p
                className={cx(
                  "mt-0.5 text-lg font-semibold tabular-nums",
                  margin === null
                    ? "text-slate-400"
                    : margin < 20
                      ? "text-rose-600"
                      : margin < 40
                        ? "text-amber-600"
                        : "text-emerald-600",
                )}
              >
                {margin === null ? "—" : `${margin}%`}
              </p>
              {price !== null && cost !== null && price > 0 ? (
                <p className="mt-0.5 text-xs text-slate-500">
                  {formatPlain(price - cost)} on {formatPlain(price)} of price
                </p>
              ) : (
                <p className="mt-0.5 text-xs text-slate-500">
                  Enter a price and a cost to see the margin.
                </p>
              )}
            </div>
          </div>
        </Card>

        {product ? (
          <Card>
            <h2 className="mb-3 text-sm font-semibold">Visibility</h2>
            <Check
              id="is_active"
              label="Active"
              checked={form.is_active ?? true}
              onChange={(v) => set("is_active", v)}
            />
            <p className="mt-2 text-xs text-slate-500">
              Retiring hides it from new bookings but keeps its history.
            </p>
          </Card>
        ) : null}
      </div>

      <div className="lg:col-span-3">
        {error && !Object.keys(fieldErrors).length ? (
          <p role="alert" className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error.message}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <Button type="submit" variant="primary" disabled={save.isPending}>
            {save.isPending ? "Saving…" : product ? "Save changes" : "Create product"}
          </Button>
          <Button onClick={() => navigate("/catalog")} disabled={save.isPending}>
            Cancel
          </Button>
        </div>
      </div>
    </form>
  );
}

function amountOf(money: Money | undefined): number | null {
  if (!money) return null;
  const value = Number(money.amount);
  return Number.isNaN(value) ? null : value;
}

function formatPlain(value: number): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return String(value);
  }
}

function TextField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
  required,
  autoFocus,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
  required?: boolean | undefined;
  autoFocus?: boolean | undefined;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        required={required}
        autoFocus={autoFocus}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-invalid={errors?.length ? true : undefined}
        className={cx(
          "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
          errors?.length ? "ring-rose-400" : "ring-slate-300 focus:ring-slate-900",
        )}
      />
      {errors?.length ? (
        <p role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function NumberField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
}: {
  id: string;
  label: string;
  value: number | null | undefined;
  onChange: (value: number | null) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
}) {
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <input
        id={id}
        type="number"
        min={0}
        // '' is meaningful here: it is how "use the account default" is expressed.
        value={value ?? ""}
        onChange={(e) =>
          onChange(e.target.value === "" ? null : Number(e.target.value))
        }
        aria-invalid={errors?.length ? true : undefined}
        className={cx(
          "mt-1 w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
          errors?.length ? "ring-rose-400" : "ring-slate-300 focus:ring-slate-900",
        )}
      />
      {errors?.length ? (
        <p role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

/**
 * Money input that speaks the API's `{amount, currency}` shape.
 *
 * The currency comes from the value the API sent rather than being typed, so a
 * client cannot accidentally post dollars against a rupee account.
 */
function MoneyField({
  id,
  label,
  value,
  onChange,
  errors,
  hint,
}: {
  id: string;
  label: string;
  value: Money | undefined;
  onChange: (value: Money | undefined) => void;
  errors?: string[] | undefined;
  hint?: string | undefined;
}) {
  const currency = value?.currency ?? "INR";
  return (
    <div>
      <label htmlFor={id} className="block text-sm font-medium text-slate-700">
        {label}
      </label>
      <div className="mt-1 flex gap-2">
        <input
          id={id}
          type="number"
          step="0.01"
          min={0}
          value={value?.amount ?? ""}
          onChange={(e) =>
            onChange(
              e.target.value === ""
                ? undefined
                : { amount: e.target.value, currency },
            )
          }
          aria-invalid={errors?.length ? true : undefined}
          className={cx(
            "w-full rounded-md border-0 px-3 py-2 text-sm ring-1 ring-inset placeholder:text-slate-400 focus:ring-2 focus:ring-inset",
            errors?.length ? "ring-rose-400" : "ring-slate-300 focus:ring-slate-900",
          )}
        />
        <span className="flex items-center rounded-md bg-slate-100 px-3 text-sm text-slate-500">
          {currency}
        </span>
      </div>
      {errors?.length ? (
        <p role="alert" className="mt-1 text-sm text-rose-600">
          {errors.join(" ")}
        </p>
      ) : hint ? (
        <p className="mt-1 text-xs text-slate-500">{hint}</p>
      ) : null}
    </div>
  );
}

function Check({
  id,
  label,
  checked,
  onChange,
}: {
  id: string;
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}) {
  return (
    <label htmlFor={id} className="flex cursor-pointer items-center gap-2 text-sm">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="size-4 rounded border-slate-300"
      />
      <span className="text-slate-700">{label}</span>
    </label>
  );
}